
import React, { useState, useMemo, useEffect } from 'react';
import {
  FileText, Plus, Search, Clock, ChevronRight, Hash, Trash2, Calendar, Filter, UserCheck, Download, Upload, CheckSquare, Square, AlertTriangle, User, ArrowUp, ArrowDown, ArrowUpDown, BarChart3, CheckCircle2, Edit3, Target, Printer
} from 'lucide-react';
import { DocumentStatus, DocumentResult, Document, User as UserType, Contact, UserRole } from '../types';
import DocumentDetailModal from './OfficialLetterDetailModal';
import ImportDocumentsModal from './ImportDocumentsModal';
import SectionDashboard from './SectionDashboard';
import { defaultDocumentTypes } from '../data';

interface DocumentListProps {
  letters: Document[];
  users: UserType[];
  contacts: Contact[];
  onUpdateLetter: (id: string, updates: Partial<Document>) => void;
  onDeleteLetter: (id: string) => void;
  onOpenCreate: () => void;
  onViewContact: (contactId: string) => void;
  currentUser: UserType;
  openLetterId?: string | null;
  onBulkDeleteDocuments?: (ids: string[]) => Promise<void>;
  containerId?: string;
  onRefresh?: () => void;
}

type SortKey = 'identification' | 'type' | 'subject' | 'requester' | 'responsible' | null;

const DocumentList: React.FC<DocumentListProps> = ({ letters, users, contacts, onUpdateLetter, onDeleteLetter, onOpenCreate, onViewContact, currentUser, openLetterId, onBulkDeleteDocuments, containerId, onRefresh }) => {
  const [selectedDocumentId, setSelectedDocumentId] = useState<string | null>(null);
  const [selectedDocumentIds, setSelectedDocumentIds] = useState<Set<string>>(new Set());
  const [openDeleteMode, setOpenDeleteMode] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState<string>('Todos');
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);

  // Estado de Ordenação
  const [sortConfig, setSortConfig] = useState<{ key: SortKey; direction: 'asc' | 'desc' }>({ key: null, direction: 'asc' });
  const [showDashboard, setShowDashboard] = useState(false);
  const [defaultSort, setDefaultSort] = useState<'recent' | 'oldest' | 'modified' | 'identification'>('recent');

  // Filtro temporal do dashboard
  const [dashPeriod, setDashPeriod] = useState<'7d' | '1m' | 'all' | 'custom'>('all');
  const [dashStart, setDashStart] = useState('');
  const [dashEnd, setDashEnd] = useState('');

  const handlePrintDashboard = () => {
    // Impressão sempre em tema claro para não sair fundo escuro no papel
    const root = document.documentElement;
    const wasDark = root.classList.contains('dark');
    if (wasDark) root.classList.remove('dark');
    const restore = () => { if (wasDark) root.classList.add('dark'); };
    window.addEventListener('afterprint', restore, { once: true });
    setTimeout(() => { window.print(); setTimeout(restore, 500); }, 60);
  };

  useEffect(() => {
    if (openLetterId) {
      setSelectedDocumentId(openLetterId);
    }
  }, [openLetterId]);

  const filteredDocuments = useMemo(() => {
    return letters.filter(l => {
      // Resolve requester name for search
      const requester = l.requester_type === 'contact' 
        ? contacts.find(c => c.id === l.requester_id)
        : users.find(u => u.id === l.requester_id);
      const requesterName = requester?.name || '';

      const matchesSearch = (l.subject || '').toLowerCase().includes(searchTerm.toLowerCase()) || 
        (l.recipient || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (l.document_number || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        requesterName.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesType = selectedType === 'Todos' || l.document_type === selectedType;

      return matchesSearch && matchesType;
    });
  }, [letters, searchTerm, selectedType, contacts, users]);

  const sortedDocuments = useMemo(() => {
    const applyDefaultSort = (list: Document[]) => {
      const sorted = [...list];
      switch (defaultSort) {
        case 'recent': return sorted.sort((a, b) => (b.date || '').localeCompare(a.date || ''));
        case 'oldest': return sorted.sort((a, b) => (a.date || '').localeCompare(b.date || ''));
        case 'modified': return sorted.sort((a, b) => {
          const lastA = a.history?.[a.history.length - 1]?.timestamp || a.date || '';
          const lastB = b.history?.[b.history.length - 1]?.timestamp || b.date || '';
          return lastB.localeCompare(lastA);
        });
        case 'identification': return sorted.sort((a, b) => (b.document_number || '').localeCompare(a.document_number || ''));
        default: return sorted;
      }
    };

    if (!sortConfig.key) return applyDefaultSort(filteredDocuments);

    const sorted = [...filteredDocuments].sort((a, b) => {
      let valA = '';
      let valB = '';

      switch (sortConfig.key) {
        case 'identification':
          valA = a.document_number || '';
          valB = b.document_number || '';
          break;
        case 'type':
          valA = a.document_type || '';
          valB = b.document_type || '';
          break;
        case 'subject':
          valA = a.subject || '';
          valB = b.subject || '';
          break;
        case 'requester': {
          const reqA = a.requester_type === 'contact' ? contacts.find(c => c.id === a.requester_id) : users.find(u => u.id === a.requester_id);
          const reqB = b.requester_type === 'contact' ? contacts.find(c => c.id === b.requester_id) : users.find(u => u.id === b.requester_id);
          valA = reqA?.name || '';
          valB = reqB?.name || '';
          break;
        }
        case 'responsible': {
          const respA = users.find(u => u.id === a.responsible_id);
          const respB = users.find(u => u.id === b.responsible_id);
          valA = respA?.name || '';
          valB = respB?.name || '';
          break;
        }
        default:
          return 0;
      }

      return sortConfig.direction === 'asc'
        ? valA.localeCompare(valB)
        : valB.localeCompare(valA);
    });

    return sorted;
  }, [filteredDocuments, sortConfig, contacts, users, defaultSort]);

  const selectedDocument = useMemo(() => {
    return letters.find(l => l.id === selectedDocumentId) || null;
  }, [selectedDocumentId, letters]);

  const getStatusStyle = (status: DocumentStatus) => {
    switch (status) {
      case DocumentStatus.ELABORATING: return 'bg-amber-100 text-amber-600 border-amber-200 dark:bg-amber-900/20 dark:text-amber-400 dark:border-amber-900/30';
      case DocumentStatus.FILED: return 'bg-blue-100 text-blue-600 border-blue-200 dark:bg-blue-900/20 dark:text-blue-400 dark:border-blue-900/30';
      case DocumentStatus.ANSWERED: return 'bg-emerald-100 text-emerald-600 border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-400 dark:border-emerald-900/30';
      case DocumentStatus.ARCHIVED: return 'bg-violet-100 text-violet-600 border-violet-200 dark:bg-violet-900/20 dark:text-violet-400 dark:border-violet-900/30';
      default: return 'bg-slate-100 text-slate-600 border-slate-200 dark:bg-navy-800 dark:text-slate-400';
    }
  };

  const handleDelete = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    e.nativeEvent.stopImmediatePropagation();
    setSelectedDocumentId(id);
    setOpenDeleteMode(true);
  };

  const toggleSelectDocument = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const newSelected = new Set(selectedDocumentIds);
    if (newSelected.has(id)) newSelected.delete(id); else newSelected.add(id);
    setSelectedDocumentIds(newSelected);
  };

  const toggleSelectAllDocuments = () => {
    if (selectedDocumentIds.size === filteredDocuments.length && filteredDocuments.length > 0) {
      setSelectedDocumentIds(new Set());
    } else {
      setSelectedDocumentIds(new Set(filteredDocuments.map(d => d.id)));
    }
  };

  const handleExportDocuments = () => {
    const itemsToExport = selectedDocumentIds.size > 0 
      ? letters.filter(d => selectedDocumentIds.has(d.id))
      : filteredDocuments;

    const headers = ['Número', 'Tipo', 'Assunto', 'Solicitante', 'Requerente Interno', 'Entrada no Gabinete', 'Destinatário', 'Data', 'Status', 'Efetividade Política', 'Data da Devolutiva', 'Retorno', 'Tema', 'Responsável'];
    const csvContent = [
      headers.join(','),
      ...itemsToExport.map(d => {
        const responsibleName = users.find(u => u.id === d.responsible_id)?.name || 'Não atribuído';
        const requester = d.requester_type === 'contact'
            ? contacts.find(c => c.id === d.requester_id)
            : users.find(u => u.id === d.requester_id);
        const requesterName = requester?.name || 'Não informado';

        return [
          `"${d.document_number || 'S/N'}"`,
          `"${d.document_type}"`,
          `"${(d.subject || '').replace(/"/g, '""')}"`,
          `"${requesterName}"`,
          `"${d.internal_requester || ''}"`,
          `"${d.received_by || ''}"`,
          `"${d.recipient || ''}"`,
          `"${new Date(d.date).toLocaleDateString('pt-BR')}"`,
          `"${d.status}"`,
          `"${d.resultado || ''}"`,
          `"${d.response_date ? new Date(d.response_date).toLocaleDateString('pt-BR') : ''}"`,
          `"${(d.response_text || '').replace(/"/g, '""')}"`,
          `"${d.theme || ''}"`,
          `"${responsibleName}"`
        ].join(',');
      })
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `documentos_export_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleConfirmBulkDelete = async () => {
    if (onBulkDeleteDocuments) {
        await onBulkDeleteDocuments(Array.from(selectedDocumentIds));
        setSelectedDocumentIds(new Set());
    }
    setShowBulkDeleteConfirm(false);
  };

  const handleSort = (key: SortKey) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const renderSortIcon = (key: SortKey) => {
    if (sortConfig.key !== key) return <ArrowUpDown size={12} className="opacity-0 group-hover:opacity-50 transition-opacity" />;
    return sortConfig.direction === 'asc' ? <ArrowUp size={12} className="text-parliament-600" /> : <ArrowDown size={12} className="text-parliament-600" />;
  };

  return (
    <div className="space-y-12 animate-in fade-in duration-500 relative print:space-y-0">
      {/* BULK DELETE CONFIRMATION */}
      {showBulkDeleteConfirm && (
        <div className="fixed inset-0 z-[150] bg-navy-950/80 backdrop-blur-sm flex flex-col items-center justify-center p-8 text-center animate-in fade-in zoom-in-95">
           <div className="w-full max-w-md bg-white dark:bg-navy-900 p-8 rounded-[32px] border border-neutral-200 dark:border-navy-800 shadow-2xl" onClick={e => e.stopPropagation()}>
             <div className="w-20 h-20 bg-red-500/20 rounded-[24px] flex items-center justify-center mb-6 shadow-xl mx-auto text-red-500"><Trash2 size={32} /></div>
             <h3 className="text-3xl font-black text-navy-950 dark:text-white tracking-tighter mb-2">Excluir {selectedDocumentIds.size} Documentos?</h3>
             <p className="text-slate-500 dark:text-slate-400 font-medium mb-8">Esta ação moverá os registros selecionados para a lixeira.</p>
             <div className="flex gap-4 justify-center">
                <button onClick={() => setShowBulkDeleteConfirm(false)} className="px-8 py-3 bg-neutral-100 dark:bg-navy-800 text-slate-600 dark:text-slate-300 rounded-xl font-black text-[10px] uppercase tracking-widest hover:bg-neutral-200 transition-all">Cancelar</button>
                <button onClick={handleConfirmBulkDelete} className="px-8 py-3 bg-red-600 text-white rounded-xl font-black text-[10px] uppercase shadow-xl hover:scale-105 transition-all">Confirmar</button>
             </div>
           </div>
        </div>
      )}

      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 px-2 print:hidden">
        <div>
          <h2 className="text-4xl font-black text-navy-950 dark:text-white tracking-tighter leading-none">Documentos</h2>
          <p className="text-slate-500 dark:text-slate-400 mt-2 font-medium">Controle de expedientes oficiais e legislativos.</p>
        </div>
        
        <div className="flex items-center gap-3">
           <button onClick={() => setShowDashboard(!showDashboard)} className={`px-5 py-4 rounded-2xl transition-all shadow-lg border flex items-center gap-2 font-black text-[10px] uppercase tracking-widest hover:scale-[1.02] active:scale-95 ${showDashboard ? 'bg-parliament-600 text-white border-transparent shadow-parliament-600/30' : 'bg-white dark:bg-navy-900 text-parliament-600 border-parliament-200 dark:border-parliament-900/50 hover:bg-parliament-50 dark:hover:bg-navy-800'}`} title="Dashboard de Métricas">
              <BarChart3 size={20} /> Dashboard
           </button>
           <button onClick={handleExportDocuments} className="p-4 bg-white dark:bg-navy-900 text-slate-500 dark:text-slate-400 rounded-2xl hover:bg-neutral-50 dark:hover:bg-navy-800 transition-all shadow-sm border border-neutral-200 dark:border-navy-800 flex items-center gap-2 font-bold text-xs" title="Exportar Documentos">
              <Download size={20} /> {selectedDocumentIds.size > 0 ? `(${selectedDocumentIds.size})` : ''}
           </button>
           {containerId && (currentUser.permissions.canEditDocuments || currentUser.role !== UserRole.LEADERSHIP) && (
             <button onClick={() => setShowImportModal(true)} className="p-4 bg-white dark:bg-navy-900 text-slate-500 dark:text-slate-400 rounded-2xl hover:bg-neutral-50 dark:hover:bg-navy-800 transition-all shadow-sm border border-neutral-200 dark:border-navy-800 flex items-center gap-2 font-bold text-xs" title="Importar Planilha">
                <Upload size={20} /> Importar
             </button>
           )}

           {/* Botão Novo Documento sempre visível se tiver permissão ou não for liderança */}
           {(currentUser.permissions.canEditDocuments || currentUser.role !== UserRole.LEADERSHIP) && (
             <button onClick={onOpenCreate} className="p-4 bg-navy-950 dark:bg-parliament-600 text-white rounded-2xl font-black text-[10px] uppercase tracking-widest shadow-xl shadow-navy-900/10 transition-all flex items-center gap-2 hover:scale-[1.02] active:scale-95 border border-transparent">
               <Plus size={20} /> Novo Documento
             </button>
           )}

           {selectedDocumentIds.size > 0 && onBulkDeleteDocuments && (
              <button 
                onClick={() => setShowBulkDeleteConfirm(true)} 
                className="p-4 bg-red-50 dark:bg-red-900/20 text-red-600 rounded-2xl hover:bg-red-100 dark:hover:bg-red-900/40 transition-all shadow-sm border border-red-100 dark:border-red-900/30 flex gap-2 font-bold text-xs items-center animate-in slide-in-from-right-2" 
                title="Excluir Selecionados"
              >
                  <Trash2 size={20} />
                  Excluir ({selectedDocumentIds.size})
              </button>
           )}
        </div>
      </div>

      <div className="bg-white dark:bg-navy-900 rounded-[40px] border border-slate-200 dark:border-navy-800 shadow-sm overflow-hidden mx-2 print:border-0 print:shadow-none print:rounded-none print:mx-0 print:overflow-visible">
        <div className="p-8 border-b border-slate-100 dark:border-navy-800 flex flex-col md:flex-row gap-6 bg-slate-50/30 dark:bg-navy-950/30 print:hidden">
          <div className="relative flex-1 group">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300 group-focus-within:text-parliament-600 transition-colors" size={20} />
            <input 
              type="text" 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Número, assunto, solicitante ou destinatário..." 
              className="w-full pl-12 pr-6 py-3 bg-white dark:bg-navy-900 border border-slate-200 dark:border-navy-700 rounded-2xl text-sm font-medium focus:ring-2 focus:ring-parliament-600 outline-none dark:text-white transition-all"
            />
          </div>
          <div className="flex items-center gap-2 bg-white dark:bg-navy-900 p-1.5 rounded-2xl border border-slate-200 dark:border-navy-700 shadow-sm overflow-x-auto">
             <button onClick={() => setSelectedType('Todos')} className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all whitespace-nowrap ${selectedType === 'Todos' ? 'bg-navy-950 dark:bg-white text-white dark:text-navy-950' : 'text-slate-400 hover:bg-slate-50 dark:hover:bg-navy-800'}`}>Todos</button>
             {defaultDocumentTypes.slice(0,4).map(type => (
                <button key={type} onClick={() => setSelectedType(type)} className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all whitespace-nowrap ${selectedType === type ? 'bg-navy-950 dark:bg-white text-white dark:text-navy-950' : 'text-slate-400 hover:bg-slate-50 dark:hover:bg-navy-800'}`}>{type}</button>
             ))}
          </div>
          <div className="flex items-center gap-2">
            <ArrowUpDown size={14} className="text-slate-400 flex-shrink-0" />
            <select value={defaultSort} onChange={e => { setDefaultSort(e.target.value as any); setSortConfig({ key: null, direction: 'asc' }); }} className="bg-white dark:bg-navy-900 border border-slate-200 dark:border-navy-700 rounded-xl px-3 py-2 text-[10px] font-black uppercase tracking-widest text-slate-600 dark:text-slate-400 outline-none">
              <option value="recent">Mais Recentes</option>
              <option value="oldest">Mais Antigos</option>
              <option value="modified">Recém Modificados</option>
              <option value="identification">Identificação</option>
            </select>
          </div>
        </div>

        {showDashboard && (() => {
          const now = new Date();
          let periodStart: Date | null = null;
          let periodEnd: Date | null = null;
          if (dashPeriod === '7d') { periodStart = new Date(now); periodStart.setDate(now.getDate() - 7); }
          else if (dashPeriod === '1m') { periodStart = new Date(now); periodStart.setMonth(now.getMonth() - 1); }
          else if (dashPeriod === 'custom') {
            if (dashStart) periodStart = new Date(dashStart + 'T00:00:00');
            if (dashEnd) periodEnd = new Date(dashEnd + 'T23:59:59.999');
          }

          const all = sortedDocuments.filter(d => {
            if (!periodStart && !periodEnd) return true;
            if (!d.date) return false;
            const t = new Date(d.date).getTime();
            if (periodStart && t < periodStart.getTime()) return false;
            if (periodEnd && t > periodEnd.getTime()) return false;
            return true;
          });

          const periodLabel = dashPeriod === '7d' ? 'Últimos 7 dias'
            : dashPeriod === '1m' ? 'Último mês'
            : dashPeriod === 'custom' ? `${dashStart ? new Date(dashStart + 'T12:00:00').toLocaleDateString('pt-BR') : 'Início'} até ${dashEnd ? new Date(dashEnd + 'T12:00:00').toLocaleDateString('pt-BR') : 'hoje'}`
            : 'Todo o período';

          const total = all.length;
          const elaborating = all.filter(d => d.status === 'Em Elaboração').length;
          const filed = all.filter(d => d.status === 'Protocolado').length;
          const answered = all.filter(d => d.status === 'Respondido').length;
          const archived = all.filter(d => d.status === 'Arquivado').length;

          const pieData = [
            { name: 'Em Elaboração', value: elaborating, color: '#f59e0b' },
            { name: 'Protocolado', value: filed, color: '#3b82f6' },
            { name: 'Respondido', value: answered, color: '#10b981' },
            { name: 'Arquivado', value: archived, color: '#8b5cf6' },
            { name: 'Cancelado', value: all.filter(d => d.status === 'Cancelado').length, color: '#ef4444' },
          ];

          const resultCount = (r: DocumentResult) => all.filter(d => d.resultado === r).length;
          const fulfilled = resultCount(DocumentResult.FULFILLED);
          const partial = resultCount(DocumentResult.PARTIAL);
          const effectivenessPieData = [
            { name: 'Atendido', value: fulfilled, color: '#15803d' },
            { name: 'Atendido Parcialmente', value: partial, color: '#22d3ee' },
            { name: 'Negado', value: resultCount(DocumentResult.DENIED), color: '#f97316' },
            { name: 'Inviável', value: resultCount(DocumentResult.UNFEASIBLE), color: '#3b82f6' },
            { name: 'Ignorado', value: resultCount(DocumentResult.IGNORED), color: '#ef4444' },
            { name: 'Arquivado', value: resultCount(DocumentResult.ARCHIVED), color: '#8b5cf6' },
            { name: 'Sem registro', value: all.filter(d => !d.resultado).length, color: '#cbd5e1' },
          ];

          const typeMap: Record<string, number> = {};
          all.forEach(d => { typeMap[d.document_type] = (typeMap[d.document_type] || 0) + 1; });
          const barData = Object.entries(typeMap).map(([name, value]) => ({ name: name.length > 12 ? name.substring(0, 12) + '…' : name, value })).sort((a, b) => b.value - a.value);

          const themeMap: Record<string, number> = {};
          all.forEach(d => { const t = d.theme || 'Sem tema'; themeMap[t] = (themeMap[t] || 0) + 1; });
          const themeBarData = Object.entries(themeMap)
            .map(([name, value]) => ({ name: name.length > 14 ? name.substring(0, 14) + '…' : name, value }))
            .sort((a, b) => b.value - a.value);

          const monthMap: Record<string, number> = {};
          all.forEach(d => { if (d.date) { const m = d.date.substring(0, 7); monthMap[m] = (monthMap[m] || 0) + 1; } });
          const areaData = Object.entries(monthMap).sort(([a],[b]) => a.localeCompare(b)).slice(-6).map(([m, v]) => ({ name: m.substring(5) + '/' + m.substring(2, 4), value: v }));

          const requesterMap: Record<string, number> = {};
          all.forEach(d => {
            const req = d.requester_type === 'contact'
              ? contacts.find(c => c.id === d.requester_id)
              : users.find(u => u.id === d.requester_id);
            const name = req?.name || (d.internal_requester ? 'Interno' : 'Não informado');
            requesterMap[name] = (requesterMap[name] || 0) + 1;
          });
          const requesterBarData = Object.entries(requesterMap)
            .map(([name, value]) => ({ name: name.length > 14 ? name.substring(0, 14) + '…' : name, value }))
            .sort((a, b) => b.value - a.value);

          return (
            <div id="doc-dash-print">
              {/* Cabeçalho visível apenas na impressão */}
              <div className="hidden print:block px-2 pt-2 pb-4 border-b border-slate-200 mb-4">
                <h1 className="text-2xl font-black text-navy-950 tracking-tighter">Relatório de Documentos</h1>
                <p className="text-xs font-bold text-slate-500 mt-1">
                  Período: {periodLabel} &nbsp;•&nbsp; {total} documento{total !== 1 ? 's' : ''} &nbsp;•&nbsp; Gerado em {new Date().toLocaleDateString('pt-BR')} às {new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                </p>
              </div>

              {/* Barra de filtro temporal + impressão (não sai na impressão) */}
              <div className="flex flex-wrap items-center justify-between gap-3 px-4 pt-6 print:hidden">
                <div className="flex flex-wrap items-center gap-2">
                  <div className="flex items-center gap-1 bg-white dark:bg-navy-900 p-1.5 rounded-2xl border border-slate-200 dark:border-navy-700 shadow-sm">
                    {([['7d', '7 dias'], ['1m', '1 mês'], ['all', 'Total'], ['custom', 'Personalizado']] as const).map(([key, label]) => (
                      <button key={key} onClick={() => setDashPeriod(key)} className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all whitespace-nowrap ${dashPeriod === key ? 'bg-navy-950 dark:bg-white text-white dark:text-navy-950' : 'text-slate-400 hover:bg-slate-50 dark:hover:bg-navy-800'}`}>{label}</button>
                    ))}
                  </div>
                  {dashPeriod === 'custom' && (
                    <div className="flex items-center gap-2 animate-in slide-in-from-left-2">
                      <input type="date" value={dashStart} onChange={e => setDashStart(e.target.value)} className="bg-white dark:bg-navy-900 border border-slate-200 dark:border-navy-700 rounded-xl px-3 py-2 text-xs font-bold text-slate-600 dark:text-slate-300 outline-none" />
                      <span className="text-[10px] font-black text-slate-400 uppercase">até</span>
                      <input type="date" value={dashEnd} onChange={e => setDashEnd(e.target.value)} className="bg-white dark:bg-navy-900 border border-slate-200 dark:border-navy-700 rounded-xl px-3 py-2 text-xs font-bold text-slate-600 dark:text-slate-300 outline-none" />
                    </div>
                  )}
                  <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">{total} documento{total !== 1 ? 's' : ''} no período</span>
                </div>
                <button onClick={handlePrintDashboard} className="flex items-center gap-2 px-5 py-3 bg-navy-950 dark:bg-parliament-600 text-white rounded-2xl font-black text-[10px] uppercase tracking-widest shadow-lg hover:scale-[1.02] active:scale-95 transition-all" title="Imprimir Relatório (A4)">
                  <Printer size={16} /> Imprimir Relatório
                </button>
              </div>

              <SectionDashboard
              stats={[
                { label: 'Total de Ofícios', value: total, color: 'text-slate-500', icon: FileText },
                { label: 'Em Elaboração', value: elaborating, color: 'text-amber-500', icon: Edit3 },
                { label: 'Protocolados', value: filed, color: 'text-blue-500', icon: Hash },
                { label: 'Respondidos', value: answered, color: 'text-emerald-500', icon: CheckCircle2 },
                { label: 'Atendidos', value: fulfilled + partial, color: 'text-green-600', icon: Target },
              ]}
              pieData={pieData}
              pieTitle="Status do Processo"
              pie2Data={effectivenessPieData}
              pie2Title="Efetividade Política"
              barData={barData}
              barTitle="Por Tipo"
              barColor="#0084ff"
              bar2Data={themeBarData}
              bar2Title="Volume por Tema"
              bar2Color="#8b5cf6"
              areaData={areaData}
              areaTitle="Evolução Mensal"
              extraBarData={requesterBarData}
              extraBarTitle="Volume por Liderança / Solicitante"
              extraBarColor="#10b981"
              />
            </div>
          );
        })()}

        {!showDashboard && <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="bg-slate-50 dark:bg-navy-950 text-slate-400 text-[10px] uppercase tracking-[0.2em] font-black border-b border-slate-200 dark:border-navy-800">
                <th className="px-4 py-5 w-10 text-center">
                   <button onClick={toggleSelectAllDocuments} className="text-slate-400 hover:text-navy-900 dark:hover:text-white">
                      {selectedDocumentIds.size > 0 && selectedDocumentIds.size === filteredDocuments.length ? <CheckSquare size={18} /> : <Square size={18} />}
                   </button>
                </th>
                
                <th onClick={() => handleSort('identification')} className="px-8 py-5 cursor-pointer group hover:bg-slate-100 dark:hover:bg-navy-800 transition-colors">
                  <div className="flex items-center gap-2">Identificação {renderSortIcon('identification')}</div>
                </th>
                
                <th onClick={() => handleSort('type')} className="px-8 py-5 cursor-pointer group hover:bg-slate-100 dark:hover:bg-navy-800 transition-colors">
                  <div className="flex items-center gap-2">Tipo / Situação {renderSortIcon('type')}</div>
                </th>
                
                <th onClick={() => handleSort('subject')} className="px-8 py-5 cursor-pointer group hover:bg-slate-100 dark:hover:bg-navy-800 transition-colors">
                  <div className="flex items-center gap-2">Assunto / Destino {renderSortIcon('subject')}</div>
                </th>
                
                <th onClick={() => handleSort('requester')} className="px-8 py-5 cursor-pointer group hover:bg-slate-100 dark:hover:bg-navy-800 transition-colors">
                  <div className="flex items-center gap-2">Solicitante {renderSortIcon('requester')}</div>
                </th>
                
                <th onClick={() => handleSort('responsible')} className="px-8 py-5 cursor-pointer group hover:bg-slate-100 dark:hover:bg-navy-800 transition-colors">
                  <div className="flex items-center gap-2">Responsável {renderSortIcon('responsible')}</div>
                </th>
                
                <th className="px-8 py-5 text-right">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-navy-800">
              {sortedDocuments.map((doc) => {
                const responsible = users.find(u => u.id === doc.responsible_id);
                const requester = doc.requester_type === 'contact' 
                    ? contacts.find(c => c.id === doc.requester_id)
                    : users.find(u => u.id === doc.requester_id);
                
                return (
                  <tr 
                    key={doc.id} 
                    onClick={() => { setSelectedDocumentId(doc.id); setOpenDeleteMode(false); }}
                    className={`hover:bg-slate-50 dark:hover:bg-navy-800 transition-colors group cursor-pointer ${selectedDocumentIds.has(doc.id) ? 'bg-slate-50 dark:bg-navy-950/30' : ''}`}
                  >
                    <td className="px-4 py-6 text-center" onClick={(e) => e.stopPropagation()}>
                       <button onClick={(e) => toggleSelectDocument(doc.id, e)} className="text-slate-300 hover:text-emerald-600">
                          {selectedDocumentIds.has(doc.id) ? <CheckSquare size={18} className="text-emerald-600" /> : <Square size={18} />}
                       </button>
                    </td>
                    <td className="px-8 py-6">
                      <div className="flex items-center gap-2">
                        <Hash size={14} className="text-parliament-400" />
                        <span className="font-black text-navy-950 dark:text-white text-xs">{doc.document_number || 'S/N'}</span>
                      </div>
                    </td>
                    <td className="px-8 py-6">
                      <div className="flex flex-col items-start gap-1">
                         <span className="text-[10px] font-black text-parliament-600 uppercase tracking-widest">{doc.document_type || 'Geral'}</span>
                         <span className={`px-3 py-1 rounded-full text-[8px] font-black uppercase tracking-widest border ${getStatusStyle(doc.status)}`}>
                           {doc.status}
                         </span>
                         {doc.deadline && (() => {
                           const days = Math.ceil((new Date(doc.deadline).getTime() - Date.now()) / (1000*60*60*24));
                           if (days <= 0) return <span className="px-2 py-0.5 rounded-full text-[7px] font-black uppercase bg-red-100 text-red-600 border border-red-200 animate-pulse">Vencido</span>;
                           if (days <= 7) return <span className="px-2 py-0.5 rounded-full text-[7px] font-black uppercase bg-amber-100 text-amber-600 border border-amber-200">{days}d restantes</span>;
                           return null;
                         })()}
                      </div>
                    </td>
                    <td className="px-8 py-6">
                      <div>
                        <p className="text-sm font-black text-navy-950 dark:text-white group-hover:text-parliament-600 transition-colors line-clamp-1">{doc.subject || 'Sem assunto'}</p>
                        <p className="text-[10px] text-slate-400 font-bold uppercase mt-1 tracking-widest flex items-center gap-2">
                           <Calendar size={12} /> {doc.recipient || 'Não informado'}
                        </p>
                      </div>
                    </td>
                    <td className="px-8 py-6">
                       <div className="flex items-center gap-2">
                          <div className="w-7 h-7 bg-emerald-50 text-emerald-600 rounded-lg flex items-center justify-center font-black text-[10px]">
                             {requester?.name?.charAt(0) || '?'}
                          </div>
                          <div>
                              <p className="text-xs font-bold text-slate-800 dark:text-slate-200 line-clamp-1">{requester?.name || 'Não informado'}</p>
                              <p className="text-[9px] font-bold text-slate-400 uppercase">{doc.requester_type === 'contact' ? 'Externo' : 'Interno'}</p>
                          </div>
                       </div>
                    </td>
                    <td className="px-8 py-6">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 bg-parliament-50 text-parliament-600 rounded-lg flex items-center justify-center font-black text-[10px]">{responsible?.name?.charAt(0) || '?'}</div>
                        <p className="text-xs font-bold text-slate-800 dark:text-slate-200 line-clamp-1">{responsible?.name || 'Não atribuído'}</p>
                      </div>
                    </td>
                    <td className="px-8 py-6 text-right">
                       <div className="flex items-center justify-end gap-2">
                          {(currentUser.permissions.canEditDocuments || currentUser.role !== UserRole.LEADERSHIP) && (
                            <button 
                              onClick={(e) => handleDelete(e, doc.id)}
                              className="p-3 text-slate-300 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950/20 rounded-xl transition-all opacity-0 group-hover:opacity-100 relative z-10"
                            >
                              <Trash2 size={20} />
                            </button>
                          )}
                          <button className="p-2.5 text-slate-300 group-hover:text-parliament-600 transition-all">
                             <ChevronRight size={22} />
                          </button>
                       </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>}
      </div>

      {showImportModal && containerId && (
        <ImportDocumentsModal
          onClose={() => setShowImportModal(false)}
          onRefresh={() => onRefresh?.()}
          currentUser={currentUser}
          containerId={containerId}
          contacts={contacts}
        />
      )}

      {selectedDocument && (
        <DocumentDetailModal
          letter={selectedDocument}
          users={users}
          contacts={contacts}
          onClose={() => { setSelectedDocumentId(null); setOpenDeleteMode(false); }}
          onUpdateLetter={onUpdateLetter}
          onDeleteLetter={async (id) => { onDeleteLetter(id); return true; }}
          onViewContact={onViewContact}
          currentUser={currentUser}
          initialDeleteMode={openDeleteMode}
        />
      )}
    </div>
  );
};

export default DocumentList;
